import { useState } from 'react';
import { message } from 'antd';
import { Task, Template } from './types';

export const useTextLabelApi = (username: string) => {
  const [projects, setProjects] = useState<string[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // 获取项目列表
  const fetchProjects = async () => {
    if (!username) {
      return;
    }
    
    try {
      const res = await fetch('/text_label/api/projects');
      
      if (!res.ok) {
        console.error('fetchProjects: HTTP error:', res.status, res.statusText);
        message.error(`获取项目列表失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('fetchProjects: Invalid content type:', contentType);
        const text = await res.text();
        console.error('fetchProjects: Response text:', text);
        message.error('获取项目列表失败: 服务器返回格式错误');
        return;
      }
      
      const data = await res.json();
      setProjects(data);
    } catch (e) {
      console.error('fetchProjects: error:', e);
      message.error('获取项目列表失败');
    }
  };

  // 获取模板列表
  const fetchTemplates = async () => {
    if (!username) {
      return;
    }
    
    try {
      const res = await fetch('/text_label/api/prompt-templates');
      
      if (!res.ok) {
        console.error('fetchTemplates: HTTP error:', res.status, res.statusText);
        message.error(`获取模板列表失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('fetchTemplates: Invalid content type:', contentType);
        const text = await res.text();
        console.error('fetchTemplates: Response text:', text);
        message.error('获取模板列表失败: 服务器返回格式错误');
        return;
      }
      
      const data = await res.json();
      setTemplates(data);
    } catch (e) {
      console.error('fetchTemplates: error:', e);
      message.error('获取模板列表失败');
    }
  };

  // 获取任务列表
  const fetchTasks = async (pageNum = 1, size = 10) => {
    if (!username) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/text_label/api/tasks?username=${encodeURIComponent(username)}&page=${pageNum}&page_size=${size}`);
      
      if (!res.ok) {
        console.error('fetchTasks: HTTP error:', res.status, res.statusText);
        message.error(`获取任务列表失败: ${res.status} ${res.statusText}`);
        return;
      }
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('fetchTasks: Invalid content type:', contentType);
        const text = await res.text();
        console.error('fetchTasks: Response text:', text);
        message.error('获取任务列表失败: 服务器返回格式错误');
        return;
      }
      
      const data = await res.json();
      setTasks(data.tasks || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 10);
    } catch (e) {
      console.error('fetchTasks: error:', e);
      message.error('获取任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 下载任务结果
  const handleDownload = (task_id: string) => {
    if (!username) {
      message.error('用户未登录');
      return;
    }
    window.open(`/text_label/api/tasks/${task_id}/download?username=${encodeURIComponent(username)}`);
  };

  // 提交文本标签识别任务
  const handleTaskSubmit = async (formData: FormData) => {
    if (!username) {
      message.error('用户未登录');
      return false;
    }
    
    setSubmitLoading(true);
    try {
      const res = await fetch('/text_label/api/tasks', {
        method: 'POST',
        body: formData,
      });
      
      if (!res.ok) {
        message.error(`任务提交失败: ${res.status} ${res.statusText}`);
        return false;
      }
      
      let data: any = {};
      try {
        data = await res.json();
      } catch (e) {
        message.error('服务端返回格式错误');
        return false;
      }
      
      if (data.task_id) {
        message.success('任务提交成功！');
        fetchTasks(page, pageSize);
        return true;
      } else {
        message.error('任务提交失败');
        return false;
      }
    } catch (e) {
      message.error('网络异常或表单校验失败');
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  // 提交模板
  const handleTemplateSubmit = async (values: any, editingTemplate: Template | null) => {
    if (!username) {
      message.error('用户未登录');
      return false;
    }
    
    setSubmitLoading(true);
    try {
      // 处理examples数据格式
      let processedValues = { ...values };
      if (values.examples && Array.isArray(values.examples)) {
        const examplesArray = values.examples.filter((example: any) => example.input && example.output);
        if (examplesArray.length > 0) {
          const tagObject: any = {};
          const inputObject: any = {};
          examplesArray.forEach((example: any, index: number) => {
            const key = (index + 1).toString();
            tagObject[key] = example.output;
            inputObject[key] = example.input;
          });
          
          processedValues.examples = {
            input: JSON.stringify(inputObject),
            output: JSON.stringify({ tag: tagObject })
          };
        }
      }
      
      // 处理label_judgement和label_system字段
      try {
        if (values.label_judgement) {
          try {
            processedValues.label_judgement = JSON.parse(values.label_judgement);
          } catch (e) {
            processedValues.label_judgement = values.label_judgement;
          }
        }
        if (values.label_system) {
          try {
            processedValues.label_system = JSON.parse(values.label_system);
          } catch (e) {
            processedValues.label_system = values.label_system;
          }
        }
      } catch (e) {
        // 处理字段时出错，使用原始值
      }
      
      const url = editingTemplate 
        ? `/text_label/api/prompt-templates/${editingTemplate._id}?username=${encodeURIComponent(username)}`
        : `/text_label/api/prompt-templates?username=${encodeURIComponent(username)}`;
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(processedValues),
      });
      
      if (!res.ok) {
        message.error(`模板${editingTemplate ? '更新' : '创建'}失败: ${res.status} ${res.statusText}`);
        return false;
      }
      
      let data: any = {};
      try {
        data = await res.json();
      } catch (e) {
        message.error('服务端返回格式错误');
        return false;
      }
      
      if (data.success || data.id || data._id || data.message === 'Template updated successfully' || data.message === 'Template created successfully') {
        message.success(`模板${editingTemplate ? '更新' : '创建'}成功！`);
        fetchTemplates();
        return true;
      } else {
        message.error(`模板${editingTemplate ? '更新' : '创建'}失败: ${data.message || '未知错误'}`);
        return false;
      }
    } catch (e) {
      message.error('网络异常或表单校验失败');
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  // 删除模板
  const handleDeleteTemplate = async (template: Template) => {
    if (!username) {
      message.error('用户未登录');
      return false;
    }
    
    try {
      const res = await fetch(`/text_label/api/prompt-templates/${template._id}?username=${encodeURIComponent(username)}`, {
        method: 'DELETE',
      });
      
      if (!res.ok) {
        message.error(`删除失败: ${res.status} ${res.statusText}`);
        return false;
      }
      
      message.success('删除成功！');
      fetchTemplates();
      return true;
    } catch (e) {
      message.error('删除失败');
      return false;
    }
  };

  return {
    projects,
    templates,
    tasks,
    total,
    page,
    pageSize,
    loading,
    submitLoading,
    fetchProjects,
    fetchTemplates,
    fetchTasks,
    handleDownload,
    handleTaskSubmit,
    handleTemplateSubmit,
    handleDeleteTemplate
  };
};
